import React, { memo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { PAGE_VIEW_OPTIONS } from "constants/common";

const Pagination = memo(
  ({ currentPage, totalPages, onPageChange, itemsPerPage, totalItems, setItemsPerPage }) => {
    // Guard against undefined/NaN when e.g. reading from cache with wrong keys
    const page = Math.max(1, Number(currentPage) || 1);
    const perPage = Math.max(1, Number(itemsPerPage) || 10);
    const total = Math.max(0, Number(totalItems) || 0);
    const pages = Math.max(1, Number(totalPages) || Math.ceil(total / perPage) || 1);

    // Helper function to generate page numbers with ellipsis
    const generatePageNumbers = () => {
      const pageNumbers = [];
      const maxVisiblePages = 7; // Maximum number of page buttons to show

      if (pages <= maxVisiblePages) {
        // If total pages is less than or equal to maxVisiblePages, show all
        for (let i = 1; i <= pages; i++) {
          pageNumbers.push(i);
        }
      } else {
        // Always show first page
        pageNumbers.push(1);

        if (page <= 4) {
          // Show first 5 pages, then ellipsis, then last page
          for (let i = 2; i <= 5; i++) {
            pageNumbers.push(i);
          }
          pageNumbers.push("...");
          pageNumbers.push(pages);
        } else if (page >= pages - 3) {
          // Show first page, ellipsis, then last 5 pages
          pageNumbers.push("...");
          for (let i = pages - 4; i <= pages; i++) {
            pageNumbers.push(i);
          }
        } else {
          // Show first page, ellipsis, current page and neighbors, ellipsis, last page
          pageNumbers.push("...");
          for (let i = page - 1; i <= page + 1; i++) {
            pageNumbers.push(i);
          }
          pageNumbers.push("...");
          pageNumbers.push(pages);
        }
      }

      return pageNumbers;
    };
    const prevDisabled = page === 1;
    const nextDisabled = page === pages;
    const navBtnClass =
      "inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 shadow-sm transition-colors hover:bg-gray-50 hover:border-gray-300 hover:text-gray-900 disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:ring-offset-0";

    return (
      <div className="flex flex-col gap-4 border-t border-s360-stroke1 bg-s360-stripe px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
        {/* Mobile: Prev/Next */}
        <div className="flex justify-between gap-2 sm:hidden">
          <button
            onClick={() => onPageChange(Math.max(page - 1, 1))}
            disabled={prevDisabled}
            className="inline-flex min-w-[100px] items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:opacity-50"
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </button>
          <button
            onClick={() => onPageChange(Math.min(page + 1, pages))}
            disabled={nextDisabled}
            className="inline-flex min-w-[100px] items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:opacity-50"
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        {/* Desktop: Left block - rows per page + summary */}
        <div className="hidden sm:flex sm:flex-1 sm:items-center sm:gap-6">
          <div className="flex items-center gap-2">
            <label htmlFor="itemsPerPage" className="text-sm font-medium text-gray-600">
              Rows
            </label>
            <select
              id="itemsPerPage"
              value={perPage}
              onChange={(e) => setItemsPerPage(parseInt(e.target.value))}
              className="h-9 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            >
              {PAGE_VIEW_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
          <p className="text-sm text-gray-500">
            <span className="font-medium text-gray-700">{(page - 1) * perPage + 1}</span>
            {" – "}
            <span className="font-medium text-gray-700">{Math.min(page * perPage, total)}</span>
            {" of "}
            <span className="font-medium text-gray-700">{total}</span>
          </p>
        </div>

        {/* Desktop: Page navigation */}
        <div className="hidden sm:flex sm:items-center sm:gap-2">
          <nav className="flex items-center gap-1" aria-label="Pagination">
            <button
              onClick={() => onPageChange(Math.max(page - 1, 1))}
              disabled={prevDisabled}
              className={navBtnClass}
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div className="flex items-center gap-1">
              {generatePageNumbers().map((pageNum, index) => (
                <React.Fragment key={index}>
                  {pageNum === "..." ? (
                    <span className="flex h-9 w-9 items-center justify-center text-sm text-gray-400">
                      …
                    </span>
                  ) : (
                    <button
                      onClick={() => onPageChange(pageNum)}
                      aria-label={`Page ${pageNum}`}
                      aria-current={page === pageNum ? "page" : undefined}
                      className={
                        page === pageNum
                          ? "inline-flex h-9 min-w-[2.25rem] items-center justify-center rounded-lg bg-s360-violet px-3 text-sm font-semibold text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-s360-violet/20 focus:ring-offset-0"
                          : "inline-flex h-9 min-w-[2.25rem] items-center justify-center rounded-lg border border-gray-200 bg-white px-3 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:ring-offset-0"
                      }
                    >
                      {pageNum}
                    </button>
                  )}
                </React.Fragment>
              ))}
            </div>
            <button
              onClick={() => onPageChange(Math.min(page + 1, pages))}
              disabled={nextDisabled}
              className={navBtnClass}
              aria-label="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </nav>
        </div>
      </div>
    );
  },
);

export default Pagination;
